import { useEffect, useState } from "react";
import { Button, Modal, Separator, Typography } from "@repo/design-system";
import type {
  ExactMatchColumnsForFiltersForDTO,
  IRangeBound,
  RangeColumnsForFiltersForDTO,
} from "@/api/molecularData";
import type { IFilterPanelState } from "../../hooks";
import { ExactMatchFields, RangeFiltersFields, SourceIdsField } from "./_parts";

interface IFilterPanelProps {
  isOpen: boolean;
  onClose: () => void;
  committed: IFilterPanelState;
  rangeBounds: IRangeBound[];
  onApply: (next: IFilterPanelState) => void;
}

const EMPTY: IFilterPanelState = { sourceIds: [], exactMatch: {}, ranges: {} };

export function FilterPanel({
  isOpen,
  onClose,
  committed,
  rangeBounds,
  onApply,
}: IFilterPanelProps) {
  const [draft, setDraft] = useState<IFilterPanelState>(committed);

  // Reset the draft to the committed state every time the panel opens, so
  // unapplied edits from a previous open don't leak in.
  useEffect(() => {
    if (isOpen) setDraft(committed);
  }, [isOpen, committed]);

  const handleExactMatchChange = (
    field: ExactMatchColumnsForFiltersForDTO,
    value: string,
  ) =>
    setDraft((prev) => ({
      ...prev,
      exactMatch: { ...prev.exactMatch, [field]: value },
    }));

  const handleRangeChange = (
    field: RangeColumnsForFiltersForDTO,
    next: { min: number; max: number } | undefined,
  ) =>
    setDraft((prev) => {
      const ranges = { ...prev.ranges };
      if (next) {
        ranges[field] = next;
      } else {
        delete ranges[field];
      }
      return { ...prev, ranges };
    });

  const handleApply = () => {
    onApply(draft);
    onClose();
  };

  const handleClearAll = () => setDraft(EMPTY);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Filters"
      size="large"
      isDismissable>
      <div className="flex max-h-[85vh] flex-col">
        <div className="flex items-center justify-between border-b border-borderPrimary px-6 py-4">
          <Typography variant="heading-4" className="text-contentPrimary">
            Filters
          </Typography>
          <Button
            variant="transparent"
            size="small"
            leftIcon="close"
            onPress={onClose}
          />
        </div>

        <div className="flex flex-col gap-6 overflow-y-auto px-6 py-5">
          <div className="flex flex-col gap-4">
            <Typography
              variant="paragraph-xl-bold"
              className="text-contentPrimary">
              ID lookup
            </Typography>
            <ExactMatchFields
              exactMatch={draft.exactMatch}
              onChange={handleExactMatchChange}
            />
          </div>

          <Separator />

          <SourceIdsField
            sourceIds={draft.sourceIds}
            onChange={(sourceIds) =>
              setDraft((prev) => ({ ...prev, sourceIds }))
            }
          />

          <Separator />

          <div className="flex flex-col gap-4">
            <Typography
              variant="paragraph-xl-bold"
              className="text-contentPrimary">
              Molecular properties
            </Typography>
            <RangeFiltersFields
              rangeBounds={rangeBounds}
              ranges={draft.ranges}
              onChange={handleRangeChange}
            />
          </div>
        </div>

        <div className="flex items-center justify-between border-t border-borderPrimary px-6 py-4">
          <Button variant="secondary" onPress={handleClearAll}>
            Clear all
          </Button>
          <div className="flex gap-2">
            <Button variant="secondary" onPress={onClose}>
              Cancel
            </Button>
            <Button variant="primary" onPress={handleApply}>
              Apply filters
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
